import { useEffect, useRef, useState } from "react";
import {
  LAB_REVIEW_OUTCOMES,
  LAB_WEBDOC_ACTION_LABEL,
  LAB_WEBDOC_OUTCOME_LABEL,
  type HeldLabReview,
  type LabReviewOutcome,
  type LabSampleCost,
  type LabWebdocAssessmentResult,
} from "@care-by-ella/contracts";
import {
  fetchCompletionNote,
  recordLabWebdocAssessment,
  UnauthorizedError,
} from "@/lib/clinician-api";
import { errorText } from "@/lib/error-text";
import { labOrderOnlyControl } from "@/lib/webdoc-step";
import { hiddenWhenReadOnly } from "./SupportView";
import { WebdocControlStep } from "./WebdocControlStep";
import {
  CompletionPicker,
  completionPayload,
  completionProblem,
  type CompletionValue,
} from "./CompletionPicker";

// TES-1060 (TES-1054 point E, Emelie 2026-10-01) — "Hantera provsvar från Webdoc".
//
// Emelie: Kirill "registrera att provunderlaget har bedömts", picks "Underlaget är godkänt", "Ytterligare komplettering krävs" or "Nytt prov krävs", and "skriva eller bekräfta sin medicinska bedömning och patientåterkoppling".
// "Om andra kompletteringar återstår sparas provbedömningen tills hela paketet är klart."
//
// The assessment and feedback are saved into the draft
// Kompletteringsanteckning; the server decides whether the review is applied
// now or held, and says which. A held review comes back here prefilled, to
// confirm once the package is complete.

const SAMPLE_COST_LABEL: Record<LabSampleCost, string> = {
  covered: "Omprov ingår — ingen ny betalning",
  charged: "Nytt/utökat provpaket — patienten betalar",
};

function pickerFrom(held: HeldLabReview | null): CompletionValue {
  return {
    outcome: "completion",
    items: held?.completion?.items ?? [],
    otherHeading: held?.completion?.otherHeading ?? "",
    otherText: held?.completion?.otherText ?? "",
    otherUploadAllowed: held?.completion?.otherUploadAllowed ?? false,
  };
}

function LabWebdocAssessmentDialogView({
  patientId,
  orderId,
  roundId,
  onDone,
  onClose,
  onUnauthorized,
}: {
  patientId: string;
  orderId: string;
  // The round whose note the texts (and a held review) are read from.
  roundId: string | null;
  // TES-1347 — with what became of the patient feedback.
  onDone: (
    result: Pick<LabWebdocAssessmentResult, "held" | "feedbackNotice">,
  ) => void;
  onClose: () => void;
  onUnauthorized: () => void;
}) {
  // The review saved earlier while other items were open.
  const [held, setHeld] = useState<HeldLabReview | null>(null);
  const [assessed, setAssessed] = useState(false);
  const [outcome, setOutcome] = useState<LabReviewOutcome | null>(null);
  const [sampleCost, setSampleCost] = useState<LabSampleCost | null>(null);
  const [completion, setCompletion] = useState<CompletionValue>(
    pickerFrom(null),
  );
  const [assessment, setAssessment] = useState("");
  const [feedback, setFeedback] = useState("");
  // TES-1181 — "Nytt prov krävs" waits for the remiss tick.
  const [labOrderDone, setLabOrderDone] = useState(false);
  const [requisitionRef, setRequisitionRef] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // A ref, not a dependency: an inline callback would re-fetch every render.
  const onUnauthorizedRef = useRef(onUnauthorized);
  onUnauthorizedRef.current = onUnauthorized;

  // "skriva eller bekräfta": what the draft note already says.
  useEffect(() => {
    if (!roundId) return;
    let live = true;
    fetchCompletionNote(patientId, roundId)
      .then((n) => {
        if (!live) return;
        const text = (key: string) =>
          n.sections.find((s) => s.key === key)?.content ?? "";
        setAssessment((a) => a || text("bedomning"));
        setFeedback((f) => f || text("aterkoppling"));
        const h =
          n.labAssessment?.orderId === orderId ? n.labAssessment.held : null;
        if (h) {
          setHeld(h);
          setAssessed(true);
          setOutcome((o) => o ?? h.outcome);
          setSampleCost((c) => c ?? h.sampleCost ?? null);
          setCompletion(pickerFrom(h));
        }
      })
      .catch((e) => {
        if (e instanceof UnauthorizedError) onUnauthorizedRef.current();
      });
    return () => {
      live = false;
    };
  }, [patientId, roundId, orderId]);

  const newSample = outcome === "new_sample_required";
  const asksForMore = outcome !== null && outcome !== "approved";
  const pickerValue: CompletionValue = newSample
    ? {
        ...completion,
        items: completion.items.includes("new_blood_test")
          ? completion.items
          : ["new_blood_test", ...completion.items],
      }
    : {
        ...completion,
        items: completion.items.filter((i) => i !== "new_blood_test"),
      };
  const problem = !assessed
    ? "Bekräfta att provunderlaget har bedömts."
    : outcome === null
      ? "Välj ett utfall."
      : asksForMore
        ? (completionProblem(pickerValue) ??
          (newSample && !sampleCost ? "Välj vem som betalar provet." : null) ??
          (newSample && !labOrderDone
            ? "Bekräfta att provbeställningen/remissen är skapad i Webdoc."
            : null))
        : null;

  async function submit() {
    if (problem || !outcome) return;
    setBusy(true);
    setError(null);
    try {
      const result = await recordLabWebdocAssessment(patientId, orderId, {
        assessed: true,
        outcome,
        ...(newSample && sampleCost ? { sampleCost } : {}),
        ...(asksForMore ? { completion: completionPayload(pickerValue) } : {}),
        assessment,
        patientFeedback: feedback,
        webdocControl: labOrderOnlyControl({
          needsLabOrder: newSample,
          labOrderDone,
          requisitionRef,
        }),
      });
      onDone({ held: result.held, feedbackNotice: result.feedbackNotice });
    } catch (e) {
      if (e instanceof UnauthorizedError) {
        onUnauthorized();
        return;
      }
      setError(errorText(e instanceof Error ? e.message : String(e)));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-stretch justify-center bg-black/30 sm:items-center sm:p-md">
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="lab-webdoc-title"
        className="flex h-full w-full flex-col gap-md overflow-y-auto bg-surface p-md sm:h-auto sm:max-h-[90vh] sm:max-w-lg sm:rounded-md sm:p-lg"
      >
        <h3 id="lab-webdoc-title" className="font-display text-xl">
          {LAB_WEBDOC_ACTION_LABEL}
        </h3>
        {held ? (
          <p className="rounded-md border border-brand/30 bg-brand/5 p-sm text-sm">
            Din provbedömning är sparad och väntar på övriga kompletteringar.
            Bekräfta den när alla delar är klara.
          </p>
        ) : null}
        <label className="flex items-start gap-sm text-sm">
          <input
            type="checkbox"
            checked={assessed}
            onChange={(e) => setAssessed(e.target.checked)}
            disabled={busy}
            className="mt-xs"
          />
          <span>Provunderlaget har bedömts</span>
        </label>
        <fieldset className="flex flex-col gap-xs" disabled={busy}>
          <legend className="text-sm font-semibold mb-xs">Utfall</legend>
          {LAB_REVIEW_OUTCOMES.map((o) => (
            <label key={o} className="flex items-center gap-sm text-sm">
              <input
                type="radio"
                name="lab-webdoc-outcome"
                checked={outcome === o}
                onChange={() => setOutcome(o)}
              />
              <span>{LAB_WEBDOC_OUTCOME_LABEL[o]}</span>
            </label>
          ))}
        </fieldset>
        {asksForMore ? (
          <CompletionPicker
            patientId={patientId}
            value={pickerValue}
            onChange={setCompletion}
            showOutcome={false}
            requiredItems={newSample ? ["new_blood_test"] : []}
            disabled={busy}
            onUnauthorized={onUnauthorized}
          />
        ) : null}
        {newSample ? (
          <fieldset className="flex flex-col gap-xs" disabled={busy}>
            <legend className="text-sm font-semibold mb-xs">
              Vem betalar provet?
            </legend>
            {(["covered", "charged"] as const).map((c) => (
              <label key={c} className="flex items-center gap-sm text-sm">
                <input
                  type="radio"
                  name="lab-webdoc-cost"
                  checked={sampleCost === c}
                  onChange={() => setSampleCost(c)}
                />
                <span>{SAMPLE_COST_LABEL[c]}</span>
              </label>
            ))}
          </fieldset>
        ) : null}
        <WebdocControlStep
          idPrefix="lab-webdoc"
          labOrder={newSample}
          labOrderDone={labOrderDone}
          onLabOrderDoneChange={setLabOrderDone}
          requisitionRef={{
            value: requisitionRef,
            onChange: setRequisitionRef,
          }}
          disabled={busy}
        />
        <label className="flex flex-col gap-xs text-sm">
          <span className="font-semibold">Medicinsk bedömning</span>
          <textarea
            value={assessment}
            onChange={(e) => setAssessment(e.target.value)}
            disabled={busy}
            rows={4}
            className="rounded-sm border border-surfaceMuted bg-surface p-sm text-sm"
          />
        </label>
        <label className="flex flex-col gap-xs text-sm">
          <span className="font-semibold">Återkoppling till patienten</span>
          <textarea
            value={feedback}
            onChange={(e) => setFeedback(e.target.value)}
            disabled={busy}
            rows={3}
            className="rounded-sm border border-surfaceMuted bg-surface p-sm text-sm"
          />
        </label>
        <p className="text-xs text-textSecondary">
          Sparas i utkastet till kompletteringsanteckningen. Patientens
          markering att blodprovet är lämnat är inget provsvar.
        </p>
        {error ? (
          <p className="rounded-md border border-danger/30 bg-danger/10 p-sm text-sm text-danger">
            {error}
          </p>
        ) : null}
        <div className="flex flex-wrap items-center justify-end gap-sm">
          {problem ? (
            <span className="text-xs text-textSecondary">{problem}</span>
          ) : null}
          <button
            type="button"
            onClick={onClose}
            disabled={busy}
            className="rounded-md border border-surfaceMuted px-md py-sm text-sm"
          >
            Avbryt
          </button>
          <button
            type="button"
            onClick={() => void submit()}
            disabled={busy || problem !== null}
            className="rounded-md bg-brand px-md py-sm text-sm text-textOnBrand hover:opacity-90 disabled:opacity-50"
          >
            Spara bedömningen
          </button>
        </div>
      </div>
    </div>
  );
}

// An action only: nothing to show in a read-only support view.
export const LabWebdocAssessmentDialog = hiddenWhenReadOnly(
  LabWebdocAssessmentDialogView,
);
